import { useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const MIGRATIONS = [
  {
    id: 'rebrand',
    endpoint: 'migrate-rebrand',
    body: {},
    timeout: 30000,
    title: 'Rebrand cleanup',
    text: 'Deletes the legacy gummy seed products and seeds the default 18+ categories. ~2 seconds.',
    rows: [
      ['Old products deleted', 'deleted_old_products'],
      ['Categories seeded', 'categories_seeded'],
    ],
  },
  {
    id: 'catalog',
    endpoint: 'migrate-seed-catalog',
    body: { per_keyword: 5 },
    // 5 per keyword × ~6 sec each = ~30s — safe under proxy timeouts
    timeout: 90000,
    title: 'Starter catalog (optional)',
    text: 'Imports 5 products each for vibrator / lingerie / rose toy from CJ Dropshipping. ~30 seconds.',
    rows: [
      ['Imported', 'imported'],
      ['Skipped (duplicates)', 'skipped'],
      ['Failed', 'failed'],
    ],
  },
  {
    id: 'bears',
    endpoint: 'migrate-bears',
    body: { default_price: 19.99 },
    timeout: 30000,
    title: 'Bears Collection ⭐',
    text: 'Adds or refreshes the Bears products: photos, descriptions, ingredients and Squirters variants. Prices you set in Products are kept for existing items. Safe to re-run.',
    rows: [
      ['Categories added', 'categories_added'],
      ['Products added', 'products_added'],
      ['Products updated', 'products_updated'],
    ],
  },
];

const MigrationCard = ({ m }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const run = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await axios.post(`${API}/admin/${m.endpoint}`, m.body, { headers: authHeaders(), timeout: m.timeout });
      setResult(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-border rounded-xl p-5 space-y-3 bg-card" data-testid={`migration-${m.id}`}>
      <div className="flex items-baseline justify-between">
        <h3 className="text-lg font-semibold text-foreground">{m.title}</h3>
        {result && <CheckCircle2 className="w-5 h-5 text-green-500" />}
      </div>
      <p className="text-sm text-muted-foreground">{m.text}</p>
      <Button type="button" onClick={run} disabled={loading} data-testid={`run-${m.id}-button`}>
        {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</> : 'Run'}
      </Button>
      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 text-red-500 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      {result && (
        <div className="bg-muted/40 rounded-lg p-3 text-xs space-y-1 font-mono" data-testid={`${m.id}-result`}>
          {m.rows.map(([label, key]) => (
            <div key={key}>{label}: <span className="text-accent">{result[key]}</span></div>
          ))}
        </div>
      )}
    </div>
  );
};

const ChangePassword = () => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 10) {
      toast.error('New password must be at least 10 characters');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/change-password`,
        { current_password: current, new_password: next },
        { headers: authHeaders() }
      );
      toast.success('Password changed');
      setCurrent('');
      setNext('');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-border rounded-xl p-5 space-y-4 bg-card" data-testid="change-password-form">
      <h3 className="text-lg font-semibold text-foreground">Account password</h3>
      <div>
        <Label htmlFor="current-password">Current password</Label>
        <Input id="current-password" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
      </div>
      <div>
        <Label htmlFor="new-password">New password (min 10 characters)</Label>
        <Input id="new-password" type="password" autoComplete="new-password" minLength={10} value={next} onChange={(e) => setNext(e.target.value)} required />
      </div>
      <Button type="submit" disabled={saving}>
        {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : 'Change password'}
      </Button>
    </form>
  );
};

export const AdminSetup = () => (
  <AdminLayout>
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-3xl font-heading font-bold text-foreground">Setup</h1>
        <p className="text-muted-foreground mt-1">Account and one-off data migrations. Migrations are idempotent.</p>
      </div>
      <ChangePassword />
      {MIGRATIONS.map((m) => <MigrationCard key={m.id} m={m} />)}
    </div>
  </AdminLayout>
);

export default AdminSetup;
